import { NgStyle } from '@angular/common';
import { Component, ElementRef, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { addToAppleWalletSvgString } from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-to-apple-wallet-widget/add-to-apple-wallet.const';
import { SafeHtmlPipe } from '@app/finxone-web-frontend/app/lib/pipes/SafeHtml.pipe';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CardState } from '@app/finxone-web-frontend/app/lib/state/card.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { isIOS } from '@app/finxone-web-frontend/app/lib/utils/device-detection.utils';
import { getWalletTokenList } from '@app/finxone-web-frontend/app/lib/utils/flutter.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import {
  APP_ZONES,
  BaseCssProperties,
  ProviderClass,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { combineLatest, filter, Subject, take, takeUntil } from 'rxjs';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { AutoDestroy } from '@finxone-platform/shared/base-types';

@Component({
  imports: [NgStyle, SafeHtmlPipe],
  selector: 'finxone-web-frontend-add-to-apple-wallet-widget',
  templateUrl: './add-to-apple-wallet-widget.component.html',
})
export class AddToAppleWalletWidgetComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public marginStyle: baseCssConfigStyle;
  private flutterWebView = (window as any).flutter_inappwebview;
  private currentCardId: string;
  private currentCardMaskedPan: string;
  public showAddToAppleWallet = true;
  public originalSvg = addToAppleWalletSvgString;
  public modifiedSvg: any;
  private mandatoryPadding: number;

  constructor(
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    private store: Store,
    private el: ElementRef,
    private renderer: Renderer2,
    private accountService: AccountService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.renderer.addClass(this.el.nativeElement, 'is-center');
    this.marginStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.MARGIN,
    ]);
    this.mandatoryPadding = this.calculateMandatoryPadding();
    this.resizeSVG();

    this.waitForCardDataAndCheckHideCondition();
    this.handleApplePayIntroductionPage();
  }

  private calculateHeight(width: number): number {
    return (width * 61) / 193; // Maintain aspect ratio
  }

  private resizeSVG() {
    const width = this.dataIn.attri.css_config?.width ?? 193;
    const height = this.calculateHeight(width);
    this.modifiedSvg = this.originalSvg
      .replace(/width="(\d+)"/, `width="${width}"`)
      .replace(/height="(\d+)"/, `height="${height}"`);
  }

  private waitForCardDataAndCheckHideCondition() {
    // Combine both card observables and wait for them to have valid data
    combineLatest([
      this.store.select(CardState.getCurrentCardId),
      this.store.select(CardState.getCurrentCardDetail),
    ])
      .pipe(
        // Filter out null and empty string values to ensure we have valid data
        filter(([cardId, cardDetail]) => {
          return !!(
            cardId?.length &&
            cardDetail &&
            Object.keys(cardDetail).length &&
            cardDetail?.maskedPan?.length
          );
        }),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(([cardId, cardDetail]) => {
        this.currentCardId = cardId;
        this.currentCardMaskedPan = cardDetail.maskedPan;

        this.checkHideCondition().catch((error) => {
          console.error('Error in checkHideCondition:', error);
          // Set a safe default state
          this.showAddToAppleWallet = false;
        });
      });
  }

  private async checkHideCondition() {
    // Additional safety checks
    if (!this.currentCardId || !this.currentCardMaskedPan) {
      console.warn('Card data not available, hiding Apple Wallet button');
      this.showAddToAppleWallet = false;
      return;
    }
    const deviceType = this.configService.getDeviceData().deviceType;
    const isWorkflow = this.configService.getActiveConfigType().isWorkflow;

    if (!isWorkflow) {
      //Check the device type
      if (deviceType !== 'mobile') {
        return;
      }
      // Only show the button for Apple devices
      if (!isIOS()) {
        this.showAddToAppleWallet = false;
        return;
      }
    } else {
      // is Workflow
      this.showAddToAppleWallet = true;
      return;
    }

    let parsedTokenList: any[] = [];
    const { tokenList: tokenListFromDeviceStorage } = await getWalletTokenList();
    if (tokenListFromDeviceStorage.length > 0) {
      parsedTokenList = tokenListFromDeviceStorage;
      localStorage.setItem('tokenList', JSON.stringify(parsedTokenList));
    }

    /* Check in the token list if any primaryAccountSuffix matches the last four digits of the current card ID.
      If a match is found, the 'Add to Apple Wallet' button should not be displayed,
      indicating that the card is already added to Apple Wallet.
    */
    this.showAddToAppleWallet = !parsedTokenList.some(
      (token) => token.primaryAccountSuffix === this.currentCardMaskedPan.slice(-4),
    );
    // If not showing button then is already on Apple Wallet
    this.accountService.isCardOnXPaySubject.next(!this.showAddToAppleWallet);
  }

  private handleApplePayIntroductionPage() {
    // If on apple pay introduction page, call the handler to notify that the user has seen the page
    if (window.location.href.includes('add-card-to-apple-pay') && this.flutterWebView) {
      // Wait for profile data to be available
      this.store
        .select(ProfileState.getProfile)
        .pipe(
          filter((profile) => {
            return profile != null && typeof profile.id === 'string' && profile.id.length > 0;
          }),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((profile) => {
          this.flutterWebView.callHandler('addUserApplePaySeenUsers', [profile.id]);
        });
    }
  }

  public onClickAddToAppleWallet() {
    if (this.flutterWebView) {
      this.flutterWebView
        .callHandler('addCardHandler', this.currentCardId)
        .then((successful: undefined | boolean) => {
          // if card added and we are on splash screen navigate to the apple pay added page
          if (successful && window.location.href.includes('apple-pay-add')) {
            this.router.navigateByUrl(
              `/zones/${formatNameAsUrl(APP_ZONES.CARD_MANAGEMENT_ZONE)}/card-added-to-apple-pay`,
            );
          }
        });
    }
  }

  public get buttonStyle() {
    return {
      'background-color': `${this.dataIn.attri.css_config?.backGroundColor ?? 'transparent'}`,
      ...this.marginStyle,
      padding: `${this.mandatoryPadding}px`,
    };
  }

  private calculateMandatoryPadding(): number {
    const width = this.dataIn.attri.css_config?.width || 193; // Default to 193 if undefined
    const height = this.calculateHeight(width);
    return 0.1 * height; // 10% of height as per Apple guidelines: https://developer.apple.com/wallet/add-to-apple-wallet-guidelines/
  }
}
